import { FieldErrors, useFieldArray, useFormContext } from "react-hook-form";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import Paper from "@mui/material/Paper";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import PaymentAllocationRow from "./PaymentAllocationRow";
import { formatCents } from "utils/formatCurrency";
import { PaymentFormValues } from "../PaymentSchema";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

interface PropTypes {
  resetAllocations?: React.MouseEventHandler<HTMLButtonElement>;
  autoAllocatePayment?: React.MouseEventHandler<HTMLButtonElement>;
}

export default function PaymentAllocationTable({
  resetAllocations,
  autoAllocatePayment,
}: PropTypes) {
  const { watch, formState } = useFormContext<PaymentFormValues>();
  const { fields } = useFieldArray<PaymentFormValues>({ name: "allocations" });

  const paymentAmount = watch("amount") ?? 0;
  const refundedAmount = watch("refunded") ?? 0;
  const allocationErrors: FieldErrors<PaymentFormValues>["allocations"] =
    formState.errors["allocations"];

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1 }}
      >
        <Box>
          <Typography variant="h5">
            Allocate up to {formatCents(paymentAmount - refundedAmount)} towards
            charges
          </Typography>
          {refundedAmount > 0 && (
            <Typography variant="h6" color="warning">
              ({formatCents(refundedAmount)} of this payment has been refunded)
            </Typography>
          )}
          <Typography variant="body2">
            Charges that have been paid in full are not shown.
          </Typography>
          {allocationErrors && (
            <Typography variant="body2" color="error">
              {allocationErrors?.root?.message}
            </Typography>
          )}
        </Box>
        <Box flexShrink={0}>
          <ButtonGroup variant="outlined">
            <Button onClick={autoAllocatePayment}>Auto Allocate</Button>
            <Button onClick={resetAllocations} color="error">
              Reset
            </Button>
          </ButtonGroup>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table
          sx={{ minWidth: 650 }}
          size="small"
          aria-label="payment allocations"
        >
          <TableHead>
            <TableRow>
              <TableCell>Charge</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell align="right">Deposit</TableCell>
              <TableCell align="right">Remaining</TableCell>
              <TableCell align="left">Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fields.map((field, index) => (
              <PaymentAllocationRow
                key={field.id}
                name={`allocations.${index}`}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
